import * as React from 'react';
import { cn } from '../../lib/utils';

export interface LandingStep {
  icon?: React.ReactNode;
  step: string;
  title: string;
  description: string;
}

export interface LandingStepsProps extends React.HTMLAttributes<HTMLElement> {
  heading?: string;
  subheading?: string;
  steps: LandingStep[];
  columns?: 3 | 4;
}

export const LandingSteps = React.forwardRef<HTMLElement, LandingStepsProps>(
  (
    {
      className,
      heading = 'How It Works',
      subheading,
      steps,
      columns = 3,
      ...props
    },
    ref
  ) => {
    const gridCols =
      columns === 4
        ? 'sm:grid-cols-2 lg:grid-cols-4'
        : 'md:grid-cols-3';

    return (
      <section
        ref={ref}
        className={cn('py-20 bg-muted/30', className)}
        {...props}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">{heading}</h2>
            {subheading && (
              <p className="text-lg text-muted-foreground max-w-xl mx-auto">
                {subheading}
              </p>
            )}
          </div>

          <div className={cn('grid grid-cols-1 gap-8', gridCols)}>
            {steps.map((item) => (
              <div
                key={item.step}
                className="relative bg-card border border-border rounded-xl p-6 hover:shadow-md transition-shadow"
              >
                <div className="text-xs font-bold text-primary/60 mb-3 tracking-widest">
                  STEP {item.step}
                </div>
                {item.icon && (
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary mb-4">
                    {item.icon}
                  </div>
                )}
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }
);

LandingSteps.displayName = 'LandingSteps';
